import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Database, Plus, Search, Pencil, X, CheckCircle2, ChevronLeft, ChevronRight, RotateCcw, Tags, MapPin, GraduationCap, Heart, BriefcaseBusiness, AlertCircle, RefreshCw, LoaderCircle, Briefcase } from "lucide-react";
import { masterConfigs, getMasterList, createMaster, updateMaster, updateMasterStatus, } from "../../api/mastersApi";
const emptyForm = { name_en: "", name_ta: "", status: "active", };
const normalizeStatus = (status) => String(status || "active").toLowerCase();
const getErrorMessage = (error) => error.response?.data?.message || error.message || "Something went wrong. Please try again.";

function ApiResponsePopup({ popup, onClose }) {
  useEffect(() => {
    if (!popup) return;
    const handleKeyDown = (event) => {
      if (event.key === "Escape" || event.key === "Enter") {
        onClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [popup, onClose]);
  if (!popup) return null;
  const isSuccess = popup.type === "success";
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/50 p-4 backdrop-blur-[2px]">
      <div role="alertdialog" aria-modal="true" aria-labelledby="api-popup-title" aria-describedby="api-popup-message"
        className="w-full max-w-sm overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="p-6 text-center">
          <div className={`mx-auto flex h-16 w-16 items-center justify-center rounded-full ${isSuccess ? "bg-emerald-50 text-emerald-600" : "bg-rose-50 text-rose-600"}`}>
            {isSuccess ? (
              <CheckCircle2 size={34} />
            ) : (
              <AlertCircle size={34} />
            )}
          </div>
          <h2 id="api-popup-title" className={`mt-4 text-2xl font-bold ${isSuccess ? "text-emerald-700" : "text-rose-700"}`}>{popup.title}</h2>
          <p id="api-popup-message" className="mt-2 break-words text-base leading-7 text-slate-600">{popup.message}</p>
        </div>
        <div className="border-t border-slate-100 bg-slate-50 p-4">
          <button type="button" onClick={onClose}
            className={`w-full rounded-lg px-4 py-3 text-base font-semibold text-white transition 
            ${isSuccess ? "bg-emerald-600 hover:bg-emerald-700" : "bg-rose-600 hover:bg-rose-700"}`}>
            OK
          </button>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const active = normalizeStatus(status) === "active";
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${active ? "bg-emerald-500" : "bg-slate-400"}`} />
      {active ? "Active" : "Inactive"}
    </span>
  );
}

function ValueModal({ category, config, item, referenceData, onClose, onSave, saving, }) {
  const [form, setForm] = useState(() => ({
    ...emptyForm,
    ...(item || {}),
    status: normalizeStatus(item?.status),
  }));

  const [error, setError] = useState("");
  const setField = (name, value) => {
    setForm((current) => ({ ...current, [name]: value, }));
    setError("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    if (!String(form.name_en || "").trim()) {
      setError("Please enter the English name.");
      return;
    }
    if (!String(form.name_ta || "").trim()) {
      setError("Please enter the Tamil name.");
      return;
    }
    for (const field of config.extraFields || []) {
      const value = form[field.name];
      if (value === undefined || value === null || String(value).trim() === "") {
        setError(`Please enter ${field.label}.`);
        return;
      }
      if (field.type === "number" || field.type === "select") {
        const numberValue = Number(value);
        if (!Number.isFinite(numberValue)) {
          setError(`Please enter a valid ${field.label}.`);
          return;
        }
        if (field.min !== undefined && numberValue < field.min) {
          setError(`${field.label} must be at least ${field.min}.`);
          return;
        }
        if (field.max !== undefined && numberValue > field.max) {
          setError(`${field.label} must not exceed ${field.max}.`);
          return;
        }
      }
    }

    const payload = { name_en: String(form.name_en).trim(), name_ta: String(form.name_ta).trim(), status: form.status || "active", };
    for (const field of config.extraFields || []) {
      payload[field.name] =
        field.type === "number" || field.type === "select"
          ? Number(form[field.name])
          : form[field.name];
    }

    try {
      await onSave(payload);
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  const renderExtraField = (field) => {
    const value = form[field.name] ?? "";
    if (field.type === "reference") {
      const records = referenceData[field.reference] || [];
      return (
        <select id={field.name} value={value} onChange={(event) => setField(field.name, event.target.value)} required
          className="mt-1.5 w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm 
          outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-50">
          <option value="">Select {field.label}</option>
          {records.map((record) => (
            <option key={record.rfId} value={record.rfId}>
              {record.name_en}
            </option>
          ))}
        </select>
      );
    }
    if (field.type === "select") {
      return (
        <select id={field.name} value={value} onChange={(event) => setField(field.name, event.target.value)} required className="mt-1.5 w-full rounded-lg 
        border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-50">
          <option value="">Select {field.label}</option>
          {(field.options || []).map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      );
    }
    return (
      <input id={field.name} type={field.type || "text"} min={field.min} max={field.max} value={value} onChange={(event) => setField(field.name, event.target.value)} required
        className="mt-1.5 w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-50" />
    );
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center overflow-y-auto bg-slate-950/50 p-4 backdrop-blur-[2px]">
      <div className="my-auto w-full max-w-xl overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
          <div>
            <h2 className="text-lg font-bold text-slate-800">
              {item ? "Edit Master Value" : "Add Master Value"}
            </h2>
            <p className="mt-1 text-xs text-slate-500">
              Category: {category}
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close modal" disabled={saving} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 disabled:opacity-50">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 gap-4 p-5 sm:grid-cols-2">
            <div>
              <label htmlFor="name_en" className="text-xs font-semibold text-slate-600">
                Name (English) *
              </label>
              <input id="name_en" autoFocus required maxLength={150} value={form.name_en || ""}
                onChange={(event) => setField("name_en", event.target.value)}
                placeholder={`Enter ${category}`}
                className="mt-1.5 w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm 
                outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-50"/>
            </div>
            <div>
              <label htmlFor="name_ta" className="text-xs font-semibold text-slate-600">
                பெயர் (தமிழ்) *
              </label>
              <input id="name_ta" required maxLength={150} value={form.name_ta || ""}
                onChange={(event) => setField("name_ta", event.target.value)}
                placeholder="தமிழ் பெயரை உள்ளிடவும்"
                className="mt-1.5 w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm 
                outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-50"/>
            </div>
            {(config.extraFields || []).map((field) => (
              <div key={field.name}>
                <label htmlFor={field.name} className="text-xs font-semibold text-slate-600">
                  {field.label} *
                </label>
                {renderExtraField(field)}
              </div>
            ))}
            <div>
              <label htmlFor="status" className="text-xs font-semibold text-slate-600">
                Status
              </label>
              <select id="status" value={form.status}
                onChange={(event) => setField("status", event.target.value)}
                className="mt-1.5 w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm 
                outline-none focus:border-blue-400">
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            {error && (
              <div role="alert" className="sm:col-span-2 rounded-lg border border-rose-200 bg-rose-50 p-3 text-xs text-rose-700">
                {error}
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2 border-t border-slate-100 bg-slate-50 p-4">
            <button type="button" onClick={onClose} disabled={saving} className="rounded-lg border border-slate-200 bg-white 
            px-4 py-2.5 text-xs font-semibold text-slate-600 disabled:opacity-50">
              Cancel
            </button>
            <button type="submit" disabled={saving} className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 
            text-xs font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60">
              {saving && (
                <LoaderCircle size={14} className="animate-spin" />
              )}
              {saving ? "Saving..." : item ? "Save Changes" : "Add Value"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Masters() {
  const [selectedCategory, setSelectedCategory] = useState("Gender");
  const [masters, setMasters] = useState({});
  const [referenceData, setReferenceData] = useState({ state: [], district: [], });
  const [searchCategory, setSearchCategory] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [apiPopup, setApiPopup] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);
  const [pageError, setPageError] = useState("");
  const config = masterConfigs.find((master) => master.name === selectedCategory) || masterConfigs[0];
  const selectedItems = masters[config.slug] || [];
  const loadMaster = useCallback(async (slug) => {
    const response = await getMasterList(slug);
    if (response?.success === false) {
      throw new Error(response.message || "Unable to load master records.");
    }
    const rows = Array.isArray(response?.data) ? response.data : [];
    setMasters((current) => ({ ...current, [slug]: rows, }));
    return rows;
  }, []);
  const loadReferences = useCallback(async () => {
    const [statesResult, districtsResult] = await Promise.allSettled([getMasterList("state"),
    getMasterList("district"),]);
    setReferenceData({
      state: statesResult.status === "fulfilled" && Array.isArray(statesResult.value?.data) ? statesResult.value.data : [],
      district: districtsResult.status === "fulfilled" && Array.isArray(districtsResult.value?.data) ? districtsResult.value.data : [],
    });
  }, []);
  const refreshSelected = useCallback(async () => {
    setLoading(true);
    setPageError("");
    try {
      await loadMaster(config.slug);
      if (["state", "district", "city"].includes(config.slug)) {
        await loadReferences();
      }
    } catch (err) {
      setPageError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [config.slug, loadMaster, loadReferences]);

  useEffect(() => {
    refreshSelected();
  }, [refreshSelected]);

  const filteredCategories = masterConfigs.filter((master) =>
    master.name
      .toLowerCase()
      .includes(searchCategory.trim().toLowerCase())
  );

  const filteredItems = useMemo(() => {
    const term = searchValue.trim().toLowerCase();
    return selectedItems.filter((item) => {
      const searchable = [item.name_en, item.name_ta, item.height_cm, item.weight_kg, item.paadam_number,]
        .filter((value) => value !== undefined && value !== null).join(" ").toLowerCase();
      return (
        searchable.includes(term) &&
        (!statusFilter ||
          normalizeStatus(item.status) === statusFilter)
      );
    });
  }, [selectedItems, searchValue, statusFilter]);

  const activeCount = selectedItems.filter(
    (item) => normalizeStatus(item.status) === "active"
  ).length;

  const inactiveCount = selectedItems.length - activeCount;
  const totalPages = Math.max(1, Math.ceil(filteredItems.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const startIndex = (safePage - 1) * pageSize;
  const visibleItems = filteredItems.slice(startIndex, startIndex + pageSize);
  const selectCategory = (name) => {
    setSelectedCategory(name);
    setSearchValue("");
    setStatusFilter("");
    setPage(1);
    setEditingItem(null);
    setModalOpen(false);
    setPageError("");
    setApiPopup(null);
  };

  const resetFilters = () => {
    setSearchValue("");
    setStatusFilter("");
    setPage(1);
  };

  const openAddModal = () => {
    setEditingItem(null);
    setModalOpen(true);
    setPageError("");
  };

  const openEditModal = (item) => {
    setEditingItem(item);
    setModalOpen(true);
    setPageError("");
  };

  const closeModal = () => {
    if (saving) return;
    setModalOpen(false);
    setEditingItem(null);
  };

  const saveItem = async (payload) => {
    setSaving(true);
    setPageError("");
    const isEditing = Boolean(editingItem);
    try {
      let response;
      if (isEditing) {
        const oldStatus = String(editingItem.status || "active").toLowerCase();
        const newStatus = String(payload.status || "active").toLowerCase();
        const { status, ...detailsPayload } = payload;
        const detailsChanged = Object.keys(detailsPayload).some((key) => String(detailsPayload[key] ?? "") !== String(editingItem[key] ?? ""));
        if (detailsChanged) {
          response = await updateMaster(config.slug, editingItem.rfId, detailsPayload);
          if (response?.success === false) {
            throw new Error(response.message || "Update failed.");
          }
        }
        if (newStatus !== oldStatus) {
          response = await updateMasterStatus(config.slug, editingItem.rfId, newStatus);
          if (response?.success === false) {
            throw new Error(response.message || "Status update failed.");
          }
        }
        if (!detailsChanged && newStatus === oldStatus) {
          setApiPopup({
            type: "success",
            title: "No Changes",
            message: "No changes were made to this record.",
          });
        } else {
          setApiPopup({
            type: "success",
            title: "Updated Successfully",
            message:
              response?.message ||
              `${config.name} updated successfully.`,
          });
        }
      } else {
        response = await createMaster(config.slug, payload);
        if (response?.success === false) {
          throw new Error(response.message || "Create failed.");
        }
        setApiPopup({
          type: "success",
          title: "Added Successfully",
          message:
            response?.message ||
            `${config.name} added successfully.`,
        });
      }
      setModalOpen(false);
      setEditingItem(null);
      setPage(1);
      try {
        await loadMaster(config.slug);
        if (["state", "district", "city"].includes(config.slug)) {
          await loadReferences();
        }
      } catch (refreshError) {
        setPageError(
          `The save operation succeeded, but refreshing the list failed: ${getErrorMessage(refreshError)
          }`
        );
      }
    } catch (error) {
      setApiPopup({
        type: "error",
        title: "Operation Failed",
        message: getErrorMessage(error),
      });
      throw error;
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (item) => {
    const currentStatus = String(item.status || "active").toLowerCase();
    const nextStatus = currentStatus === "active" ? "inactive" : "active";
    if (!window.confirm(`Change "${item.name_en}" to ${nextStatus}?`)) {
      return;
    }
    try {
      setStatusUpdatingId(item.rfId);
      const response = await updateMasterStatus(config.slug, item.rfId, nextStatus);
      if (response?.success === false) {
        throw new Error(response.message || "Status update failed.");
      }
      setApiPopup({
        type: "success",
        title: "Status Updated",
        message: response?.message || `Status changed to ${nextStatus}.`,
      });
      await loadMaster(config.slug);
    } catch (error) {
      setApiPopup({
        type: "error",
        title: "Status Update Failed",
        message: getErrorMessage(error),
      });
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const displayValue = (item) => {
    const extras = [];
    if (item.height_cm != null) {
      extras.push(`${item.height_cm} cm`);
    }
    if (item.weight_kg != null) {
      extras.push(`${item.weight_kg} kg`);
    }
    if (item.paadam_number != null) {
      extras.push(`Paadam ${item.paadam_number}`);
    }
    if (item.state_id != null) {
      const state = referenceData.state.find(
        (record) => Number(record.rfId) === Number(item.state_id)
      );
      if (state) extras.push(state.name_en);
    }
    if (item.district_id != null) {
      const district = referenceData.district.find(
        (record) => Number(record.rfId) === Number(item.district_id)
      );
      if (district) extras.push(district.name_en);
    }
    return extras.join(" · ");
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="mb-2 flex items-center gap-2 text-sm text-slate-400">
          <span>Home</span>
          <span>/</span>
          <span>Management</span>
          <span>/</span>
          <span className="font-medium text-blue-600">Masters</span>
        </div>
        <h1 className="text-xl font-bold text-slate-800 sm:text-2xl">Masters Management</h1>
        <p className="mt-1 text-xs text-slate-500 sm:text-sm">
          Manage matrimonial master values through the backend APIs.
        </p>
      </div>
      {pageError && (
        <div role="alert" className="flex items-start justify-between gap-3 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          <div className="flex items-start gap-2">
            <AlertCircle size={18} className="mt-0.5 shrink-0" />
            <span>{pageError}</span>
          </div>
          <button type="button" onClick={() => setPageError("")} aria-label="Dismiss error"><X size={16} /></button>
        </div>
      )}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">

        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-slate-500">Master Categories</p>
              <h3 className="mt-2 text-2xl font-bold text-slate-800">{masterConfigs.length}</h3>
              <p className="mt-1 text-xs text-slate-400">Connected master APIs</p>
            </div>
            <div className="rounded-xl bg-blue-50 p-3 text-blue-600">
              <Database size={22} />
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-slate-500">Values in Category</p>
              <h3 className="mt-2 text-2xl font-bold text-slate-800">{selectedItems.length}</h3>
              <p className="mt-1 text-xs text-slate-400">{selectedCategory}</p>
            </div>
            <div className="rounded-xl bg-violet-50 p-3 text-violet-600">
              <Tags size={22} />
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-slate-500">Active Values</p>
              <h3 className="mt-2 text-2xl font-bold text-slate-800">{activeCount}</h3>
              <p className="mt-1 text-xs text-slate-400">{inactiveCount} inactive</p>
            </div>
            <div className="rounded-xl bg-emerald-50 p-3 text-emerald-600">
              <CheckCircle2 size={22} />
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 items-start gap-5 xl:grid-cols-[250px_minmax(0,1fr)]">
        <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-100 p-4">
            <h2 className="text-sm font-bold text-slate-800">
              Master Categories</h2>
            <p className="mt-1 text-sm text-slate-400">Select a category to manage.</p>
            <div className="relative mt-4">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input value={searchCategory}
                onChange={(event) => setSearchCategory(event.target.value)}
                placeholder="Search categories"
                className="w-full rounded-lg border border-slate-200 py-2.5 pl-9 pr-3 text-xs
                 outline-none focus:border-blue-400"/>
            </div>
          </div>
          <nav className="max-h-[600px] space-y-1 overflow-y-auto p-2">
            {filteredCategories.map((master) => {
              const selected = selectedCategory === master.name;
              return (
                <button key={master.slug} type="button"
                  onClick={() => selectCategory(master.name)}
                  className={`flex w-full items-center gap-3 rounded-lg px-3 py-3 text-left transition ${selected ?
                    "bg-blue-50 font-semibold text-blue-700" : "text-slate-600 hover:bg-slate-50"}`}>
                  <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${selected ? "bg-blue-100 text-blue-600" : "bg-slate-100 text-slate-500"}`}>
                    {["State", "District", "City"].includes(master.name) ? (
                      <MapPin size={15} />
                    ) : master.name === "Education" ? (
                      <GraduationCap size={15} />
                    ) : master.name === "Designation" ? (
                      <BriefcaseBusiness size={15} />
                    ) : master.name === "Marital Status" ? (
                      <Heart size={15} />
                    ) : master.name === "Occupation" ? (
                      <Briefcase size={15} />
                    ) : (
                      <Database size={15} />
                    )}
                  </span>
                  <span className="min-w-0 flex-1 text-xs">{master.name}</span>
                </button>
              );
            })}

            {filteredCategories.length === 0 && (
              <p className="px-3 py-8 text-center text-xs text-slate-400">
                No categories found.
              </p>
            )}
          </nav>
        </section>

        <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="flex flex-col gap-3 border-b border-slate-100 p-4 sm:flex-row sm:items-center sm:justify-between sm:p-5">
            <div>
              <h2 className="text-sm font-bold text-slate-800">{selectedCategory}</h2>
              <p className="mt-1 text-sm text-slate-400">Manage values for this master category.</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button type="button" disabled={loading} onClick={refreshSelected} className="inline-flex items-center justify-center gap-2 
              rounded-lg border border-slate-200 px-3 py-2.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50">
                <RefreshCw size={14} className={loading ? "animate-spin" : ""} />Refresh
              </button>
              <button type="button" onClick={openAddModal} className="inline-flex items-center justify-center gap-2 
              rounded-lg bg-blue-600 px-4 py-2.5 text-xs font-semibold text-white hover:bg-blue-700">
                <Plus size={16} />Add Value
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 border-b border-slate-100 p-4 sm:grid-cols-2">
            <div className="relative">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input value={searchValue}
                onChange={(event) => { setSearchValue(event.target.value); setPage(1); }}
                placeholder={`Search ${selectedCategory.toLowerCase()}`}
                className="w-full rounded-lg border border-slate-200 py-2.5 pl-9 pr-3 text-xs 
                outline-none focus:border-blue-400"/>
            </div>
            <select value={statusFilter} onChange={(event) => { setStatusFilter(event.target.value); setPage(1); }}
              className="rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-xs text-slate-600 outline-none focus:border-blue-400">
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            <div className="sm:col-span-2">
              <button type="button" onClick={resetFilters} className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 
              text-xs font-semibold text-slate-600 hover:bg-slate-50">
                <RotateCcw size={13} />Reset Filters
              </button>
            </div>
          </div>

          {loading ? (
            <div className="p-14 text-center text-sm text-slate-500">
              <LoaderCircle size={24} className="mx-auto mb-3 animate-spin text-blue-600" />
              Loading {selectedCategory} records...
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[650px] text-left">
                <thead className="bg-slate-50">
                  <tr className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    <th className="px-5 py-3">ID</th>
                    <th className="px-4 py-3">Name (English)</th>
                    <th className="px-4 py-3">பெயர் (தமிழ்)</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-5 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {visibleItems.map((item) => (
                    <tr key={item.rfId} className="transition hover:bg-slate-50/70">
                      <td className="px-5 py-4 text-xs text-slate-500">{String(item.rfId).padStart(3, "0")}</td>
                      <td className="px-4 py-4">
                        <p className="text-sm text-slate-700">{item.name_en}</p>
                        {displayValue(item) && (
                          <p className="mt-1 text-xs text-slate-400">{displayValue(item)}</p>
                        )}
                      </td>
                      <td className="px-4 py-4 text-sm text-slate-700">{item.name_ta}</td>
                      <td className="whitespace-nowrap px-4 py-4">
                        <button type="button" disabled={statusUpdatingId === item.rfId} title={`Change status for ${item.name_en}`}
                          onClick={() => toggleStatus(item)} className="rounded-full focus:outline-none focus:ring-2 focus:ring-blue-300 
                        disabled:cursor-wait disabled:opacity-50">
                          {statusUpdatingId === item.rfId ? (
                            <span className="inline-flex items-center gap-1 text-xs text-slate-500">
                              <LoaderCircle size={13} className="animate-spin" />
                              Updating
                            </span>
                          ) : (
                            <StatusBadge status={item.status} />
                          )}
                        </button>
                      </td>
                      <td className="whitespace-nowrap px-5 py-4">
                        <div className="flex items-center justify-end gap-1">
                          <button type="button" title="Edit value" aria-label={`Edit ${item.name_en}`} onClick={() => openEditModal(item)}
                            className="rounded-lg p-2 text-amber-600 hover:bg-amber-50">
                            <Pencil size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {visibleItems.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-5 py-14 text-center">
                        <Database size={28} className="mx-auto text-slate-300" />
                        <p className="mt-3 text-sm font-semibold text-slate-700">No master values found</p>
                        <p className="mt-1 text-xs text-slate-400">Try another search or add a new value.</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex flex-col gap-3 border-t border-slate-100 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
            <p className="text-sm text-slate-500">
              Showing {filteredItems.length ? startIndex + 1 : 0} to{" "}
              {Math.min(startIndex + pageSize, filteredItems.length)} of{" "}
              {filteredItems.length} entries
            </p>
            <div className="flex items-center justify-between gap-3 sm:justify-end">
              <div className="flex items-center gap-2 text-sm text-slate-500">Show
                <select value={pageSize} onChange={(event) => {
                  setPageSize(Number(event.target.value));
                  setPage(1);
                }} className="rounded-md border border-slate-200 bg-white px-2 py-1.5">
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                </select>
              </div>
              <div className="flex items-center gap-1">
                <button type="button" disabled={safePage <= 1}
                  onClick={() => setPage((current) => Math.max(1, current - 1))}
                  aria-label="Previous page"
                  className="rounded-lg border border-slate-200 p-2 text-slate-500 disabled:opacity-40">
                  <ChevronLeft size={14} />
                </button>
                <span className="px-2 text-xs text-slate-600">{safePage} / {totalPages}</span>
                <button type="button" disabled={safePage >= totalPages} onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
                  aria-label="Next page" className="rounded-lg border border-slate-200 p-2 text-slate-500 disabled:opacity-40">
                  <ChevronRight size={14} />
                </button>
              </div>
            </div>
          </div>
        </section>
      </div>

      {modalOpen && (
        <ValueModal category={selectedCategory} config={config} item={editingItem} referenceData={referenceData}
          saving={saving} onClose={closeModal} onSave={saveItem} />
      )}

      <ApiResponsePopup popup={apiPopup} onClose={() => setApiPopup(null)} />

      <style>{`
        @keyframes popupIn {
          from {
            opacity: 0;
            transform: translateY(8px) scale(0.97);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
      `}</style>
    </div>
  );
}